import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TitleIcon from '@mui/icons-material/Title';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import ImageIcon from '@mui/icons-material/Image';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import RichTextEditorImageControls from './RichTextEditorImageControls';

// `variant`: 'basic' offers bold and italic only; 'full' (the default) is the whole toolbar —
// headings, underline, alignment, lists, blockquote and, when `onImageUpload` is given, images.
const RichTextEditorToolbar = ({
  editor,
  variant = 'full',
  onImageUpload,
  selectedImage,
  imageMenuAnchor,
  setImageMenuAnchor,
  handleImageAlign,
  handleImageSize,
}) => {
  const isFull = variant === 'full';

  const handleImageUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const url = await onImageUpload(file);
        editor.chain().focus().setImage({ src: url }).run();
      }
    };
    input.click();
  };

  return (
    <Box className="p-2 flex flex-wrap gap-1">
      {isFull && (
        <>
          <Tooltip title="Normal Text">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setParagraph().run()}
              className={editor.isActive('paragraph') ? 'bg-green-50 text-green-600' : ''}
            >
              <TextFormatIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Heading 1">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={
                editor.isActive('heading', { level: 1 }) ? 'bg-green-50 text-green-600' : ''
              }
            >
              <TitleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Heading 2">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={
                editor.isActive('heading', { level: 2 }) ? 'bg-green-50 text-green-600' : ''
              }
            >
              <TitleIcon fontSize="small" style={{ transform: 'scale(0.9)' }} />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
        </>
      )}
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-green-50 text-green-600' : ''}
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-green-50 text-green-600' : ''}
        >
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isFull && (
        <>
          <Tooltip title="Underline">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={editor.isActive('underline') ? 'bg-green-50 text-green-600' : ''}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Align Left">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              className={editor.isActive({ textAlign: 'left' }) ? 'bg-green-50 text-green-600' : ''}
            >
              <FormatAlignLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              className={
                editor.isActive({ textAlign: 'center' }) ? 'bg-green-50 text-green-600' : ''
              }
            >
              <FormatAlignCenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              className={
                editor.isActive({ textAlign: 'right' }) ? 'bg-green-50 text-green-600' : ''
              }
            >
              <FormatAlignRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Bullet List">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={editor.isActive('bulletList') ? 'bg-green-50 text-green-600' : ''}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={editor.isActive('orderedList') ? 'bg-green-50 text-green-600' : ''}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Indent">
            {/* Handle tooltip on disabled button: https://github.com/mui/material-ui/issues/8416#issuecomment-332556082 */}
            <div>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
                disabled={!editor.can().sinkListItem('listItem')}
              >
                <FormatIndentIncreaseIcon fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title="Outdent">
            {/* Handle tooltip on disabled button: https://github.com/mui/material-ui/issues/8416#issuecomment-332556082 */}
            <div>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().liftListItem('listItem').run()}
                disabled={!editor.can().liftListItem('listItem')}
              >
                <FormatIndentDecreaseIcon fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Blockquote">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={editor.isActive('blockquote') ? 'bg-green-50 text-green-600' : ''}
            >
              <FormatQuoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          {onImageUpload && (
            <>
              <Tooltip title="Insert Image">
                <IconButton size="small" onClick={handleImageUploadClick}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <RichTextEditorImageControls
                editor={editor}
                selectedImage={selectedImage}
                imageMenuAnchor={imageMenuAnchor}
                setImageMenuAnchor={setImageMenuAnchor}
                handleImageAlign={handleImageAlign}
                handleImageSize={handleImageSize}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default RichTextEditorToolbar;
